import { useState, useEffect } from 'react';
import { X, UserCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Agency {
  name: string;
}

interface Assistant {
  id: string;
  firstname: string;
  lastname: string;
  agencies: {
    name: string;
  }[];
}

interface AssistantModalProps {
  assistant: Assistant | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AssistantModal({ assistant, isOpen, onClose, onSuccess }: AssistantModalProps) {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    agencies: [] as { name: string }[],
    firstname: '',
    lastname: ''
  });

  useEffect(() => {
    loadAgencies();
  }, []);

  useEffect(() => {
    if (assistant) {
      setFormData({
        agencies: assistant.agencies,
        firstname: assistant.firstname,
        lastname: assistant.lastname
      });
    } else {
      setFormData({
        agencies: [{ name: '' }],
        firstname: '',
        lastname: ''
      });
    }
  }, [assistant]);

  const loadAgencies = async () => {
    const { data, error } = await supabase
      .from('agencies')
      .select('name')
      .order('name');

    if (error) {
      setError(error.message);
      return;
    }

    setAgencies(data || []);
  };

  const handleAddAgency = () => {
    setFormData(prev => ({
      ...prev,
      agencies: [...prev.agencies, { name: '' }]
    }));
  };

  const handleRemoveAgency = (index: number) => {
    setFormData(prev => ({
      ...prev,
      agencies: prev.agencies.filter((_, i) => i !== index)
    }));
  };

  const handleAgencyChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      agencies: prev.agencies.map((agency, i) => 
        i === index ? { name: value } : agency
      )
    }));
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (assistant) {
        // Update existing assistant
        const { error: updateError } = await supabase
          .from('assistants')
          .update({
            firstname: formData.firstname.trim(),
            lastname: formData.lastname.trim()
          })
          .eq('id', assistant.id);

        if (updateError) throw updateError;

        // Delete existing agency relationships
        const { error: deleteError } = await supabase
          .from('assistant_agencies')
          .delete()
          .eq('assistant_id', assistant.id);

        if (deleteError) throw deleteError;

        // Insert new agency relationships
        const { error: insertError } = await supabase
          .from('assistant_agencies')
          .insert(
            formData.agencies.map(agency => ({
              assistant_id: assistant.id,
              agency: agency.name
            }))
          );

        if (insertError) throw insertError;
      } else {
        // Create new assistant
        const { data: newAssistant, error: createError } = await supabase
          .from('assistants')
          .insert([{
            firstname: formData.firstname.trim(),
            lastname: formData.lastname.trim()
          }])
          .select()
          .single();

        if (createError) throw createError;

        // Create agency relationships
        const { error: relationError } = await supabase
          .from('assistant_agencies')
          .insert(
            formData.agencies.map(agency => ({
              assistant_id: newAssistant.id,
              agency: agency.name
            }))
          );

        if (relationError) throw relationError;
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <UserCircle size={18} className="text-white/80" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">
                {assistant ? "Modifier l'assistante" : 'Nouvelle assistante'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-5">

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={formData.firstname}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstname: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.lastname}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastname: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                  required
                />
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-slate-700">Agences</h3>
                <button
                  type="button"
                  onClick={handleAddAgency}
                  className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors text-sm font-medium px-3"
                >
                  + Ajouter une agence
                </button>
              </div>

              {formData.agencies.map((agency, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1">
                    <select
                      value={agency.name}
                      onChange={(e) => handleAgencyChange(index, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                      required
                    >
                      <option value="">Sélectionner une agence</option>
                      {agencies.map((a) => (
                        <option key={a.name} value={a.name}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  {formData.agencies.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveAgency(index)}
                      className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors shrink-0"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Chargement…' : (assistant ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}